import React, { useState, useEffect, useRef } from 'react';
import { X, Cookie, Shield, BarChart3 } from 'lucide-react';
import ReactGA from 'react-ga4';
import { Link } from 'react-router-dom';

const CookieConsentManager = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false
  });

  const pageStartTime = useRef(Date.now());
  const currentPage = useRef(window.location.pathname);
  const lastActivityTime = useRef(Date.now());
  const pageViewId = useRef(generatePageViewId());
  const timeTrackingInterval = useRef(null);
  const isTrackingActive = useRef(false);
  const sessionId = useRef(getOrCreateSessionId());
  const userId = useRef(getOrCreateUserId());
  const ga4Initialized = useRef(false);
  
  const maxScrollDepth = useRef(0);
  const scrollCheckInterval = useRef(null);
  const clickCount = useRef(0);

  const GA4_MEASUREMENT_ID = 'G-WRQHY8T7Z1';

  function generatePageViewId() {
    return 'pv_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9);
  }

  function getOrCreateSessionId() {
    let sessionId = sessionStorage.getItem('session_id');
    if (!sessionId) {
      sessionId = 'sess_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9);
      sessionStorage.setItem('session_id', sessionId);
    }
    return sessionId;
  }

  function getOrCreateUserId() {
    const cookieUserId = getCookie('user_id');
    if (cookieUserId) return cookieUserId;
    
    const newUserId = 'user_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9);
    setCookie('user_id', newUserId, 730);
    return newUserId;
  }

  function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift() || null;
    return null;
  }

  function setCookie(name, value, days = 365) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = `expires=${date.toUTCString()}`;
    document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax`;
  }

  const getDeviceInfo = () => {
    const ua = navigator.userAgent;
    let device = 'Desktop';
    let browser = 'Unknown';
    let os = 'Unknown';

    if (/mobile/i.test(ua)) device = 'Mobile';
    else if (/tablet|ipad/i.test(ua)) device = 'Tablet';

    if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Chrome')) browser = 'Chrome';
    else if (ua.includes('Safari')) browser = 'Safari';
    else if (ua.includes('Edge')) browser = 'Edge';
    else if (ua.includes('Opera')) browser = 'Opera';

    if (ua.includes('Windows')) os = 'Windows';
    else if (ua.includes('Mac')) os = 'MacOS';
    else if (ua.includes('Linux')) os = 'Linux';
    else if (ua.includes('Android')) os = 'Android';
    else if (ua.includes('iOS')) os = 'iOS';

    return {
      device,
      browser,
      os,
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      viewport_size: `${window.innerWidth}x${window.innerHeight}`,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
  };

  const getReferralSource = () => {
    const referrer = document.referrer;
    const urlParams = new URLSearchParams(window.location.search);
    
    const utmSource = urlParams.get('utm_source');
    const utmMedium = urlParams.get('utm_medium');
    const utmCampaign = urlParams.get('utm_campaign');
    const utmContent = urlParams.get('utm_content');
    const utmTerm = urlParams.get('utm_term');
    
    if (utmSource) {
      return {
        source: utmSource,
        medium: utmMedium || '(none)',
        campaign: utmCampaign || '(not set)',
        content: utmContent || '(not set)',
        term: utmTerm || '(not set)',
        type: 'utm_campaign',
        referrer_url: referrer || '(direct)'
      };
    }
    
    if (!referrer) {
      return {
        source: '(direct)',
        medium: '(none)',
        campaign: '(direct)',
        content: '(not set)',
        term: '(not set)',
        type: 'direct',
        referrer_url: '(direct)'
      };
    }
    
    let referrerDomain;
    try {
      referrerDomain = new URL(referrer).hostname.toLowerCase();
    } catch (e) {
      return {
        source: '(direct)',
        medium: '(none)',
        campaign: '(direct)',
        content: '(not set)',
        term: '(not set)',
        type: 'direct',
        referrer_url: '(direct)'
      };
    }
    
    const searchEngines = {
      'google': { name: 'google', param: 'q' },
      'bing': { name: 'bing', param: 'q' },
      'yahoo': { name: 'yahoo', param: 'p' },
      'duckduckgo': { name: 'duckduckgo', param: 'q' },
      'baidu': { name: 'baidu', param: 'wd' },
      'yandex': { name: 'yandex', param: 'text' }
    };
    
    for (const [domain, config] of Object.entries(searchEngines)) {
      if (referrerDomain.includes(domain)) {
        let searchTerm = '(not provided)';
        try {
          const referrerUrl = new URL(referrer);
          searchTerm = referrerUrl.searchParams.get(config.param) || '(not provided)';
        } catch (e) {}
        
        return {
          source: config.name,
          medium: 'organic',
          campaign: '(organic)',
          content: '(not set)',
          term: searchTerm,
          type: 'search',
          referrer_url: referrer
        };
      }
    }
    
    const socialPlatforms = {
      'linkedin': 'linkedin',
      'facebook': 'facebook',
      'twitter': 'twitter',
      't.co': 'twitter',
      'instagram': 'instagram',
      'youtube': 'youtube',
      'reddit': 'reddit',
      'tiktok': 'tiktok'
    };
    
    for (const [domain, source] of Object.entries(socialPlatforms)) {
      if (referrerDomain.includes(domain)) {
        return {
          source: source,
          medium: 'social',
          campaign: '(social)',
          content: '(not set)',
          term: '(not set)',
          type: 'social',
          referrer_url: referrer
        };
      }
    }
    
    if (['mail.google.com', 'outlook.live.com', 'mail.yahoo.com'].some(d => referrerDomain.includes(d))) {
      return {
        source: 'email',
        medium: 'email',
        campaign: '(email)',
        content: '(not set)',
        term: '(not set)',
        type: 'email',
        referrer_url: referrer
      };
    }
    
    return {
      source: referrerDomain,
      medium: 'referral',
      campaign: '(referral)',
      content: '(not set)',
      term: '(not set)',
      type: 'referral',
      referrer_url: referrer
    };
  };

  const trackScrollDepth = () => {
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollTop = window.scrollY;
    const scrollPercent = Math.round((scrollTop / (documentHeight - windowHeight)) * 100);
    
    if (scrollPercent > maxScrollDepth.current) {
      maxScrollDepth.current = scrollPercent;
      
      if ([25, 50, 75, 100].includes(scrollPercent) && ga4Initialized.current) {
        ReactGA.event('scroll', {
          percent_scrolled: scrollPercent,
          page_location: window.location.href,
          page_path: currentPage.current,
          page_view_id: pageViewId.current,
          session_id: sessionId.current
        });
        console.log(`📜 Scroll depth: ${scrollPercent}%`);
      }
    }
  };

  const trackInteraction = (type, details) => {
    clickCount.current++;
    
    if (ga4Initialized.current && preferences.analytics) {
      ReactGA.event('user_engagement', {
        engagement_type: type,
        page_location: window.location.href,
        page_path: currentPage.current,
        page_view_id: pageViewId.current,
        session_id: sessionId.current,
        click_count: clickCount.current,
        ...details
      });
    }
  };

  const initializeGA4 = () => {
    if (ga4Initialized.current) {
      console.log('⚠️ GA4 already initialized');
      return;
    }

    console.log('🔧 Initializing React-GA4...');

    try {
      const referralData = getReferralSource();
      
      // Initialize React-GA4 with campaign params
      ReactGA.initialize(GA4_MEASUREMENT_ID, {
        gaOptions: {
          userId: userId.current,
          cookieFlags: 'SameSite=Lax',
          // Set campaign parameters at initialization
          campaignSource: referralData.source,
          campaignMedium: referralData.medium,
          campaignName: referralData.campaign,
          campaignContent: referralData.content,
          campaignKeyword: referralData.term
        },
        gtagOptions: {
          send_page_view: false,
          // Set default parameters for ALL events
          custom_map: {
            dimension1: 'traffic_source',
            dimension2: 'traffic_medium',
            dimension3: 'traffic_campaign'
          }
        }
      });

      // Set consent mode
      ReactGA.gtag('consent', 'default', {
        'analytics_storage': 'granted',
        'ad_storage': preferences.marketing ? 'granted' : 'denied',
        'ad_user_data': preferences.marketing ? 'granted' : 'denied',
        'ad_personalization': preferences.marketing ? 'granted' : 'denied'
      });

      // CRITICAL: Set campaign parameters at config level
      ReactGA.gtag('config', GA4_MEASUREMENT_ID, {
        campaign_source: referralData.source,
        campaign_medium: referralData.medium,
        campaign_name: referralData.campaign,
        campaign_content: referralData.content,
        campaign_term: referralData.term,
        user_id: userId.current
      });

      ga4Initialized.current = true;
      console.log('✅ React-GA4 Initialized');
      console.log('📊 Measurement ID:', GA4_MEASUREMENT_ID);
      console.log('🆔 User ID:', userId.current);
      console.log('🎯 Campaign Source:', referralData.source);
      console.log('📡 Campaign Medium:', referralData.medium);

      // Wait a moment then track
      setTimeout(() => {
        trackReferralSource();
        trackPageView();
      }, 100);

    } catch (error) {
      console.error('❌ Failed to initialize GA4:', error);
    }
  };

  const trackReferralSource = () => {
    if (!ga4Initialized.current) {
      console.warn('⚠️ GA4 not ready for referral tracking');
      return;
    }
    
    // Only track referral once per session
    if (sessionStorage.getItem('referral_tracked') === 'true') {
      console.log('ℹ️ Referral already tracked this session');
      return;
    }
    
    const referralData = getReferralSource();
    const deviceInfo = getDeviceInfo();
    
    // Store traffic source
    setCookie('traffic_source', JSON.stringify(referralData), 30);
    sessionStorage.setItem('referral_tracked', 'true');
    
    console.log('🎯 Traffic Source:', referralData);
    console.log('💻 Device Info:', deviceInfo);
    
    // Send first_visit or session_start event with complete data
    const isFirstVisit = !getCookie('user_returning');
    const eventName = isFirstVisit ? 'first_visit' : 'session_start';
    
    if (isFirstVisit) {
      setCookie('user_returning', 'true', 365);
    }

    ReactGA.event(eventName, {
      // Session info
      session_id: sessionId.current,
      user_id: userId.current,
      
      // Device info
      device_category: deviceInfo.device,
      browser: deviceInfo.browser,
      operating_system: deviceInfo.os,
      screen_resolution: deviceInfo.screen_resolution,
      viewport_size: deviceInfo.viewport_size,
      language: deviceInfo.language,
      timezone: deviceInfo.timezone,
      
      // Traffic source (for custom dimensions)
      traffic_source: referralData.source,
      traffic_medium: referralData.medium,
      traffic_campaign: referralData.campaign,
      traffic_type: referralData.type,
      referrer_url: referralData.referrer_url
    });

    console.log(`🚀 ${eventName} event sent with traffic source: ${referralData.source} / ${referralData.medium}`);
  };

  const trackPageView = () => {
    if (!ga4Initialized.current) {
      console.warn('⚠️ GA4 not ready for page view');
      return;
    }
    
    const deviceInfo = getDeviceInfo();
    const referralData = getReferralSource();
    
    // Send page_view with all context
    ReactGA.send({
      hitType: 'pageview',
      page: currentPage.current,
      title: document.title,
      // Custom parameters
      page_view_id: pageViewId.current,
      session_id: sessionId.current,
      user_id: userId.current,
      device_category: deviceInfo.device,
      browser: deviceInfo.browser,
      viewport: deviceInfo.viewport_size,
      // Traffic source for custom tracking
      traffic_source: referralData.source,
      traffic_medium: referralData.medium,
      traffic_campaign: referralData.campaign
    });
    
    console.log(`📄 Page View: ${currentPage.current}`);
    console.log(`   Source: ${referralData.source} | Medium: ${referralData.medium}`);
    
    if (!isTrackingActive.current) {
      isTrackingActive.current = true;
      timeTrackingInterval.current = setInterval(() => {
        trackPageEngagement();
      }, 15000);
    }
  };

  const trackPageEngagement = () => {
    if (!ga4Initialized.current) return;
    
    const timeSpent = Math.floor((Date.now() - pageStartTime.current) / 1000);
    const timeSinceLastActivity = Math.floor((Date.now() - lastActivityTime.current) / 1000);
    
    if (timeSinceLastActivity < 30) {
      ReactGA.event('user_engagement', {
        engagement_time_msec: timeSpent * 1000,
        page_location: window.location.href,
        page_path: currentPage.current,
        page_view_id: pageViewId.current,
        session_id: sessionId.current,
        scroll_depth: maxScrollDepth.current,
        click_count: clickCount.current
      });
      
      console.log(`⏱️ Engagement: ${timeSpent}s | Scroll: ${maxScrollDepth.current}% | Clicks: ${clickCount.current}`);
    }
  };

  const trackPageExit = () => {
    if (!ga4Initialized.current) return;
    
    const timeSpent = Math.floor((Date.now() - pageStartTime.current) / 1000);
    
    if (timeTrackingInterval.current) {
      clearInterval(timeTrackingInterval.current);
      timeTrackingInterval.current = null;
    }
    isTrackingActive.current = false;
    
    if (timeSpent > 0) {
      ReactGA.event('page_exit', {
        page_location: window.location.href,
        page_path: currentPage.current,
        engagement_time_msec: timeSpent * 1000,
        page_view_id: pageViewId.current,
        session_id: sessionId.current,
        scroll_depth: maxScrollDepth.current,
        total_clicks: clickCount.current
      });
      
      console.log(`🚪 Exit: ${currentPage.current} | Time: ${timeSpent}s`);
    }
  };

  useEffect(() => {
    checkCookieConsent();
    
    const trackActivity = () => {
      lastActivityTime.current = Date.now();
    };
    
    const handleClick = (e) => {
      trackActivity();
      const target = e.target;
      trackInteraction('click', {
        element_type: target.tagName.toLowerCase(),
        element_text: target.textContent?.substring(0, 50) || ''
      });
    };
    
    const handleScroll = () => {
      trackActivity();
      trackScrollDepth();
    };
    
    document.addEventListener('click', handleClick);
    document.addEventListener('scroll', handleScroll);
    document.addEventListener('keydown', trackActivity);
    
    scrollCheckInterval.current = setInterval(trackScrollDepth, 1000);
    
    const handlePageChange = () => {
      trackPageExit();
      currentPage.current = window.location.pathname;
      pageStartTime.current = Date.now();
      lastActivityTime.current = Date.now();
      pageViewId.current = generatePageViewId();
      maxScrollDepth.current = 0;
      clickCount.current = 0;
      trackPageView();
    };

    window.addEventListener('popstate', handlePageChange);
    window.addEventListener('beforeunload', trackPageExit);
    
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        trackPageExit();
      } else {
        pageStartTime.current = Date.now();
        lastActivityTime.current = Date.now();
        pageViewId.current = generatePageViewId();
        maxScrollDepth.current = 0;
        trackPageView();
      }
    });

    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('scroll', handleScroll);
      document.removeEventListener('keydown', trackActivity);
      window.removeEventListener('popstate', handlePageChange);
      window.removeEventListener('beforeunload', trackPageExit);
      if (scrollCheckInterval.current) clearInterval(scrollCheckInterval.current);
      trackPageExit();
    };
  }, []);

  const checkCookieConsent = () => {
    const consent = getCookie('cookie_consent');
    
    if (!consent) {
      setShowBanner(true);
    } else {
      try {
        const consentData = JSON.parse(consent);
        setPreferences(consentData);
        
        if (consentData.analytics) {
          console.log('✅ Analytics consent found, initializing GA4...');
          initializeGA4();
        }
      } catch (e) {
        console.error('❌ Error parsing consent:', e);
        setShowBanner(true);
      }
    }
  };

  const saveConsent = (consentPreferences) => {
    setCookie('cookie_consent', JSON.stringify(consentPreferences));
    setCookie('cookie_consent_timestamp', new Date().toISOString());
    
    setPreferences(consentPreferences);
    setShowBanner(false);
    setShowDetails(false);

    if (consentPreferences.analytics) {
      console.log('✅ Analytics enabled, initializing GA4...');
      initializeGA4();
    } else {
      console.log('⚠️ Analytics disabled by user');
    }
  };

  const handleAcceptAll = () => {
    console.log('✅ User accepted all cookies');
    saveConsent({ necessary: true, analytics: true, marketing: true });
  };

  const handleDenyAll = () => {
    console.log('⛔ User denied all optional cookies');
    saveConsent({ necessary: true, analytics: false, marketing: false });
  };

  const handleSavePreferences = () => {
    console.log('💾 User saved custom preferences:', preferences);
    saveConsent(preferences);
  };

  const togglePreference = (key) => {
    if (key === 'necessary') return;
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 p-4 z-50">
      <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-xl">
                <Cookie className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">We value your privacy</h3>
                <p className="text-sm text-gray-600">First time here? Let's set your cookie preferences.</p>
              </div>
            </div>
            <button onClick={handleDenyAll} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          {!showDetails ? (
            <>
              <p className="text-gray-700 mb-6">
                We use cookies to enhance your browsing experience, analyze site traffic, and personalize content.
              </p>
              <Link
  to="/cookies"
  className="inline-block text-sm text-purple-600 font-semibold hover:underline"
>
  Read our Cookie Policy
</Link>
              <div className="flex flex-wrap gap-3">
                <button onClick={handleAcceptAll} className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg">
                  Accept All
                </button>
                <button onClick={handleDenyAll} className="px-6 py-3 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-all">
                  Deny All
                </button>
                <button onClick={() => setShowDetails(true)} className="px-6 py-3 bg-white border-2 border-purple-600 text-purple-600 font-semibold rounded-lg hover:bg-purple-50 transition-all">
                  Customize
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                  <input type="checkbox" checked disabled className="mt-1 w-5 h-5" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Shield className="w-5 h-5 text-green-600" />
                      <h4 className="font-semibold">Necessary Cookies</h4>
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">Always Active</span>
                    </div>
                    <p className="text-sm text-gray-600">Essential for the website to function properly.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                  <input type="checkbox" checked={preferences.analytics} onChange={() => togglePreference('analytics')} className="mt-1 w-5 h-5 cursor-pointer" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <BarChart3 className="w-5 h-5 text-blue-600" />
                      <h4 className="font-semibold">Analytics Cookies</h4>
                    </div>
                    <p className="text-sm text-gray-600">
                      Tracks: referral sources (LinkedIn, Google, etc.), time spent, scroll depth, clicks, device info, and user behavior via GA4.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                  <input type="checkbox" checked={preferences.marketing} onChange={() => togglePreference('marketing')} className="mt-1 w-5 h-5 cursor-pointer" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Cookie className="w-5 h-5 text-orange-600" />
                      <h4 className="font-semibold">Marketing Cookies</h4>
                    </div>
                    <p className="text-sm text-gray-600">Used to display relevant advertisements.</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <button onClick={handleSavePreferences} className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg">
                  Save Preferences
                </button>
                <button onClick={() => setShowDetails(false)} className="px-6 py-3 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-all">
                  Back
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsentManager;